/**
 * LastVisiblePriceIndicator component displays the last visible price point
 * within the current viewport with a dot, horizontal dotted line, and Y-axis label.
 *
 * Features:
 * - Dot at the last visible data point with direction-based color (green/red/gray)
 * - Horizontal dotted line spanning the full chart width
 * - Y-axis label with arrow-shaped background showing the price
 * - Updates synchronously with zoom/pan for real-time feedback
 *
 * Uses core/indicatorUtils for pure calculation logic.
 * This component is a thin visual wrapper.
 */

import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  buildArrowPath,
  calculateIndicatorData,
  calculateLabelDimensions,
} from "~utils/ticker-chart";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";

export interface LastVisiblePriceIndicatorProps {
  /** Series ID to track */
  seriesId: string;
  /** Whether to show the horizontal line, default: true */
  showLine?: boolean;
  /** Whether to show the Y-axis label, default: true */
  showLabel?: boolean;
  /** Whether to show the dot, default: true */
  showDot?: boolean;
  /** Locale for price formatting */
  locale?: string;
  /** Dot radius, default: 4 */
  dotRadius?: number;
}

/**
 * LastVisiblePriceIndicator renders a visual indicator at the last visible price level.
 * Subscribes to 'data', 'scales', and 'viewport' events for updates on zoom/pan.
 */
const LastVisiblePriceIndicatorInner: React.FC<
  LastVisiblePriceIndicatorProps
> = ({
  seriesId,
  showLine = true,
  showLabel = true,
  showDot = true,
  locale = "en-US",
  dotRadius = 4,
}) => {
  const store = useChartContext();
  const storeRef = useStoreRef(store);

  // Subscribe to all relevant events for immediate updates during zoom/pan
  const revision = useChartSubscription(store, ["data", "viewport", "scales"]);

  const indicatorData = useMemo(() => {
    const currentStore = storeRef.current;
    const state = currentStore.getState();
    const scales = currentStore.getScales();
    const layout = currentStore.getLayout();
    const visibleData = currentStore.getVisibleData(seriesId);

    return calculateIndicatorData({
      seriesId,
      locale,
      state,
      scales,
      layout,
      visibleData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, locale]);

  if (!indicatorData) {
    return null;
  }

  const {
    dotX,
    y,
    priceLabel,
    color,
    textColor,
    yAxisOnRight,
    lineX1,
    lineX2,
    typography,
  } = indicatorData;

  // Calculate label dimensions using core utility
  const labelDims = calculateLabelDimensions({
    y,
    lineX2,
    lineX1,
    yAxisOnRight,
    priceLabel,
    typography,
  });

  const {
    arrowWidth,
    textWidth,
    labelHeight,
    totalLabelWidth,
    labelX,
    labelY,
    textX,
  } = labelDims;

  // Build arrow path using core utility
  const arrowPath = buildArrowPath({
    labelY,
    labelHeight,
    centerY: y,
    totalLabelWidth,
    arrowWidth,
    labelX,
    textWidth,
    yAxisOnRight,
  });

  return (
    <g
      className={styles.lastVisiblePriceIndicator}
      data-component="price-movement-chart-last-visible-price-indicator"
    >
      {/* Horizontal dotted line spanning full chart width */}
      {showLine && (
        <line
          className={styles.lastVisiblePriceLine}
          x1={lineX1}
          y1={y}
          x2={lineX2}
          y2={y}
          stroke={color}
          data-component="price-movement-chart-last-visible-price-line"
        />
      )}

      {/* Dot at the last visible price point */}
      {showDot && (
        <circle
          className={styles.lastVisiblePriceDot}
          cx={dotX}
          cy={y}
          r={dotRadius}
          fill={color}
          data-component="price-movement-chart-last-visible-price-dot"
        />
      )}

      {/* Y-axis label with arrow-shaped background */}
      {showLabel && (
        <g
          className={styles.lastVisiblePriceLabel}
          data-component="price-movement-chart-last-visible-price-yaxis-label"
        >
          <path
            className={styles.lastVisiblePriceLabelBg}
            d={arrowPath}
            fill={color}
          />
          <text
            className={styles.lastVisiblePriceLabelText}
            x={textX}
            y={y}
            dy="0.35em"
            textAnchor="middle"
            fontSize={typography.axisLabelSize}
            fill={textColor}
          >
            {priceLabel}
          </text>
        </g>
      )}
    </g>
  );
};

export const LastVisiblePriceIndicator = React.memo(
  LastVisiblePriceIndicatorInner
);
LastVisiblePriceIndicator.displayName = "LastVisiblePriceIndicator";
